import React from 'react';
import { X, Phone, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/ChaloAIIcon';
import { useAuth } from '@/components/AuthProvider';
import { useAuthViewModel, type AuthSuccessContext } from '@/viewmodels/auth/useAuthViewModel';
import { useLanguage } from '@/hooks/useLanguage';
import { OtpStep } from '@/components/auth/steps/OtpStep';
import { RegistrationStep } from '@/components/auth/steps/RegistrationStep';
import { isAuthStayOnPageEnabled, consumeAuthStayOnPage } from '@/utils/authStayOnPage';

interface AuthModalProps {
  mode: 'login' | 'register';
  onClose: () => void;
  onSwitchMode: (mode: 'login' | 'register') => void;
  /** Optional in-page success handler (also used with authStayOnPage session flag). */
  onAuthenticated?: (context: AuthSuccessContext) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  mode,
  onClose,
  onSwitchMode,
  onAuthenticated,
}) => {
  const { setAuthenticated } = useAuth();
  const stayOnPage = Boolean(onAuthenticated) || isAuthStayOnPageEnabled();

  const handleAuthenticated = (context: AuthSuccessContext) => {
    setAuthenticated(true);
    consumeAuthStayOnPage();
    onAuthenticated?.(context);
    onClose();
  };

  const viewModel = useAuthViewModel(
    mode,
    onClose,
    stayOnPage ? { onAuthenticated: handleAuthenticated } : {}
  );
  const { t } = useLanguage();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md transform transition-all flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
          {mode === 'login' ? (
            <h2 className="text-2xl font-bold text-gray-900">Welcome Back</h2>
          ) : (
            <Logo size={40} className="mx-auto" />
          )}
          <button
            type="button"
            onClick={() => {
              consumeAuthStayOnPage();
              onClose();
            }}
            className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Step 1: Phone Number */}
          {viewModel.step === 'phone' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-gray-600">
                  {mode === 'login' ? 'Enter your phone number to sign in' : 'Enter your phone number to get started'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex w-full">
                  {/* Country code dropdown button */}
                  <div className="relative z-10" ref={viewModel.countryDropdownRef}>
                    <button
                      type="button"
                      className="flex items-center gap-2 pl-3 pr-6 py-3 h-12 border border-gray-300 border-r-0 bg-white text-gray-700 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => viewModel.setCountryDropdownOpen(!viewModel.countryDropdownOpen)}
                      tabIndex={0}
                      style={{ minWidth: 90, maxWidth: 140 }}
                    >
                      {viewModel.selectedCountry && (
                        <>
                          <span className="font-semibold">{viewModel.selectedCountry.callingCode}</span>
                          <img
                            src={`https://flagcdn.com/24x18/${viewModel.selectedCountry.code.toLowerCase()}.png`}
                            alt="flag"
                            className="inline-block ml-2 w-5 h-4 object-cover rounded-sm border border-gray-200 bg-white"
                          />
                        </>
                      )}
                    </button>
                    {viewModel.countryDropdownOpen && (
                      <div
                        className={`absolute left-0 right-0 w-[320px] z-50 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-y-auto overflow-x-auto animate-fade-in`}
                        style={{ maxHeight: '200px' }}
                      >
                        <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                          <input
                            type="text"
                            placeholder="Search country or code"
                            value={viewModel.countrySearch}
                            onChange={e => viewModel.setCountrySearch(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                        </div>
                        <ul className="divide-y divide-gray-100">
                          {viewModel.filteredCountries.length === 0 && (
                            <li className="p-4 text-gray-500 text-center">No countries found</li>
                          )}
                          {viewModel.filteredCountries.slice(0, 20).map((c) => (
                            <li
                              key={c.callingCode}
                              className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-blue-50 ${viewModel.countryCode === c.callingCode ? 'bg-blue-100' : ''}`}
                              onClick={() => viewModel.handleCountrySelect(c)}
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  viewModel.handleCountrySelect(c);
                                }
                              }}
                            >
                              <span className="font-semibold">{c.callingCode}</span>
                              <img
                                src={`https://flagcdn.com/24x18/${c.code.toLowerCase()}.png`}
                                alt={c.name}
                                className="inline-block ml-2 w-5 h-4 object-cover rounded-sm border border-gray-200 bg-white"
                              />
                              <span className="ml-2">{c.name}</span>
                            </li>
                          ))}
                          {viewModel.filteredCountries.length > 20 && (
                            <li className="p-2 text-xs text-gray-400 text-center">Showing first 20 results. Refine your search for more.</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  {/* Phone number input */}
                  <input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={viewModel.phoneNumber}
                    onChange={e => {
                      const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                      viewModel.setPhoneNumber(digits);
                    }}
                    maxLength={10}
                    onKeyDown={e => {
                      const allowedKeys = [
                        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'
                      ];
                      if (
                        viewModel.phoneNumber.length >= 10 &&
                        !allowedKeys.includes(e.key) &&
                        !(e.ctrlKey || e.metaKey) &&
                        /^[0-9]$/.test(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    className="flex-1 h-12 py-3 px-4 border border-gray-300 border-l-0 rounded-r-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ minWidth: 0 }}
                  />
                </div>
              </div>

              <Button
                onClick={viewModel.handlePhoneSubmit}
                disabled={viewModel.loading}
                className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 py-3"
              >
                {viewModel.loading ? 'Sending...' : 'Send OTP'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <div className="text-center">
                <p className="text-gray-600">
                  {mode === 'login' ? "Don't have an account?" : "Already have an account?"}
                  <button
                    onClick={() => onSwitchMode(mode === 'login' ? 'register' : 'login')}
                    className="text-blue-600 font-semibold ml-1 hover:underline"
                  >
                    {mode === 'login' ? 'Sign Up' : 'Sign In'}
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {viewModel.step === 'otp' && <OtpStep vm={viewModel} />}

          {/* Step 3: Role Selection (Register only) */}
          {viewModel.step === 'role' && <RegistrationStep vm={viewModel} t={t} />}
        </div>
      </div>
    </div>
  );
};
